import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Eye, 
  Pencil, 
  Trash2, 
  Send, 
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  Loader2,
  X,
  Mail,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import { SortableHeader } from '@/components/SortableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  SENT: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  PAID: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  OVERDUE: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: X },
};

export default function CustomerInvoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  
  // Create invoice form state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipientEmail: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('invoiceDate');
  const [sortDir, setSortDir] = useState('desc');
  const [customerFilter, setCustomerFilter] = useState('ALL');

  useEffect(() => {
    fetchData();
    checkEmailConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/email/status`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailConfigured(res.data.configured);
    } catch (error) {
      console.error('Failed to check email config:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, jobsRes] = await Promise.all([
        axios.get(`${API}/customer-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleOpenCreateDialog = () => {
    setSelectedCustomer('');
    setSelectedJobs([]);
    setLineItems([]);
    setNotes('');
    setTaxRate(0);
    setCreateDialogOpen(true);
  };

  const handleGenerateLineItems = async () => {
    if (!selectedCustomer || selectedJobs.length === 0) {
      toast.error('Please select a customer and at least one job');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/customer-invoices/generate-from-jobs`,
        {
          customerId: selectedCustomer,
          jobIds: selectedJobs,
          includeLaborCharges: true,
          includeMaterialCharges: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLineItems(response.data.lineItems || []);
      toast.success(`Generated ${response.data.lineItems?.length || 0} line items`);
    } catch (error) {
      console.error('Failed to generate line items:', error);
      toast.error('Failed to generate line items');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: `custom-${Date.now()}`,
        description: '',
        quantity: 1,
        unitPrice: 0,
        amount: 0,
        sourceType: 'CUSTOM',
      },
    ]);
  };

  const handleUpdateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Recalculate amount if quantity or unitPrice changed
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].amount = qty * price;
    }
    
    setLineItems(updated);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleCreateInvoice = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/customer-invoices`,
        {
          customerId: selectedCustomer,
          jobIds: selectedJobs,
          lineItems: lineItems.map(item => ({
            id: item.id,
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unitPrice: parseFloat(item.unitPrice) || 0,
            amount: parseFloat(item.amount) || 0,
            jobId: item.jobId,
            sourceType: item.sourceType,
          })),
          notes,
          taxRate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invoice created successfully');
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/customer-invoices/${invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedInvoice(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus, paidDate = null, paidAmount = null) => {
    try {
      const updateData = { status: newStatus };
      if (paidDate) updateData.paidDate = paidDate;
      if (paidAmount) updateData.paidAmount = paidAmount;
      
      await axios.patch(`${API}/customer-invoices/${invoiceId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
      setViewDialogOpen(false);
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error('Failed to update invoice');
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/customer-invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${selectedInvoice?.invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deletingInvoice) return;
    
    try {
      await axios.delete(`${API}/customer-invoices/${deletingInvoice.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Invoice deleted');
      setDeleteDialogOpen(false);
      setDeletingInvoice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleOpenEmailDialog = (invoice) => {
    const customer = customers.find(c => c.id === invoice.customerId);
    setEmailingInvoice(invoice);
    setEmailForm({
      recipientEmail: customer?.billingEmail || customer?.email || '',
      subject: '',
      message: ''
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailingInvoice) return;
    if (!emailForm.recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/customer-invoices/${emailingInvoice.id}/send-email`, {
        recipientEmail: emailForm.recipientEmail,
        subject: emailForm.subject || null,
        message: emailForm.message || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invoice sent to ${emailForm.recipientEmail}`);
      setEmailDialogOpen(false);
      setEmailingInvoice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDir('asc'); }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'ALL' && inv.status !== statusFilter) return false;
    if (customerFilter !== 'ALL' && inv.customerId !== customerFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!(inv.invoiceNumber?.toLowerCase().includes(q) || inv.customerName?.toLowerCase().includes(q))) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortColumn) return 0;
    let aVal, bVal;
    switch (sortColumn) {
      case 'invoiceNumber': aVal = a.invoiceNumber || ''; bVal = b.invoiceNumber || ''; break;
      case 'customerName': aVal = a.customerName || ''; bVal = b.customerName || ''; break;
      case 'invoiceDate': aVal = a.invoiceDate || ''; bVal = b.invoiceDate || ''; break;
      case 'dueDate': aVal = a.dueDate || ''; bVal = b.dueDate || ''; break;
      case 'totalAmount': aVal = a.totalAmount || 0; bVal = b.totalAmount || 0; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      default: return 0;
    }
    if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const customerJobs = jobs.filter(j => j.customerId === selectedCustomer);

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 overflow-x-hidden" data-testid="customer-invoices-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 break-words">Customer Invoices</h1>
          <p className="mt-2 text-lg text-slate-700">Create and manage invoices for your customers</p>
        </div>
        <Button
          onClick={handleOpenCreateDialog}
          data-testid="create-invoice-button"
          className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-sm" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-64">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">Customer</Label>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="rounded-sm" data-testid="customer-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{filteredInvoices.length}</p>
            <p className="text-xs text-slate-500 uppercase">Total Invoices</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(filteredInvoices.filter(i => i.status === 'SENT').reduce((s, i) => s + i.totalAmount, 0))}
            </p>
            <p className="text-xs text-slate-500 uppercase">Outstanding</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(filteredInvoices.filter(i => i.status === 'PAID').reduce((s, i) => s + i.totalAmount, 0))}
            </p>
            <p className="text-xs text-slate-500 uppercase">Collected</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {filteredInvoices.filter(i => i.status === 'OVERDUE').length}
            </p>
            <p className="text-xs text-slate-500 uppercase">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {invoices.length > 0 && (
        <div className='mb-4'>
          <div className='relative max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input data-testid='customer-invoice-search' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search by invoice # or customer...' className='pl-9 rounded-sm border-2' />
          </div>
        </div>
      )}

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No invoices found</p>
            <Button
              onClick={handleOpenCreateDialog}
              variant="outline"
              className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
            >
              Create your first invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm rounded-sm overflow-hidden">
          <div className="overflow-x-auto min-w-0">
          <Table className="min-w-[600px]" data-testid='customer-invoices-table'>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <SortableHeader column='invoiceNumber' label='Invoice #' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='customerName' label='Customer' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='invoiceDate' label='Date' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='dueDate' label='Due Date' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='totalAmount' label='Amount' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} className='text-right' />
                <SortableHeader column='status' label='Status' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Actions</th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((invoice) => {
                const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || FileText;
                const statusCfg = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.DRAFT;
                
                return (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono font-bold">{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.customerName}</TableCell>
                    <TableCell>{invoice.invoiceDate}</TableCell>
                    <TableCell>{invoice.dueDate}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(invoice.totalAmount)}</TableCell>
                    <TableCell>
                      <Badge className={`${statusCfg.color} rounded-sm`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewInvoice(invoice)}
                          data-testid={`view-invoice-${invoice.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {emailConfigured && invoice.status !== 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => handleOpenEmailDialog(invoice)}
                            data-testid={`email-invoice-${invoice.id}`}
                            title="Send via email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        {invoice.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setDeletingInvoice(invoice);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-invoice-${invoice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </Card>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Create Invoice</DialogTitle>
            <DialogDescription>
              Select a customer and jobs to generate an invoice
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Customer & Jobs Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Customer *
                </Label>
                <Select value={selectedCustomer} onValueChange={(v) => {
                  setSelectedCustomer(v);
                  setSelectedJobs([]);
                  setLineItems([]);
                }}>
                  <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="invoice-customer-select">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Jobs
                </Label>
                <div className="mt-2 max-h-32 overflow-y-auto border-2 border-slate-200 rounded-sm p-2 space-y-1">
                  {customerJobs.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      {selectedCustomer ? 'No jobs for this customer' : 'Select a customer first'}
                    </p>
                  ) : (
                    customerJobs.map(job => (
                      <label key={job.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <Checkbox
                          checked={selectedJobs.includes(job.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedJobs([...selectedJobs, job.id]);
                            } else {
                              setSelectedJobs(selectedJobs.filter(id => id !== job.id));
                            }
                          }}
                        />
                        <span className="font-mono">{job.jobNumber}</span>
                        <span className="text-slate-500">- {job.description}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleGenerateLineItems}
                disabled={!selectedCustomer || selectedJobs.length === 0 || generating}
                variant="outline"
                className="border-2 border-[#1A3668] text-[#1A3668] rounded-sm"
                data-testid="generate-line-items-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Generate Line Items from Jobs
                  </>
                )}
              </Button>
            </div>

            {/* Line Items Table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Line Items
                </Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleAddLineItem}
                  className="text-[#1A3668]"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Custom Line
                </Button>
              </div>
              
              <div className="border-2 border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[40%]">Description</TableHead>
                      <TableHead className="w-[15%]">Qty</TableHead>
                      <TableHead className="w-[15%]">Unit Price</TableHead>
                      <TableHead className="w-[20%] text-right">Amount</TableHead>
                      <TableHead className="w-[10%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                          No line items yet. Generate from jobs or add custom lines.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => handleUpdateLineItem(index, 'description', e.target.value)}
                              placeholder="Description"
                              className="h-8 text-sm rounded-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateLineItem(index, 'quantity', e.target.value)}
                              className="h-8 text-sm rounded-sm"
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleUpdateLineItem(index, 'unitPrice', e.target.value)}
                              className="h-8 text-sm rounded-sm"
                              min="0"
                              step="0.01"
                            />
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(item.amount)}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveLineItem(index)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Tax & Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-mono font-bold">{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">Tax Rate (%):</span>
                  <Input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                    className="w-20 h-8 text-sm rounded-sm"
                    min="0"
                    step="0.1"
                    data-testid="tax-rate-input"
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax:</span>
                  <span className="font-mono">{formatCurrency(calculateTax())}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-bold text-slate-800">Total:</span>
                  <span className="font-mono font-bold text-lg">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Notes (Optional)
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes or terms..."
                rows={3}
                className="mt-2 rounded-sm border-2"
                data-testid="invoice-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateInvoice}
              disabled={submitting || !selectedCustomer || lineItems.length === 0}
              className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
              data-testid="submit-invoice-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Create Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              Invoice {selectedInvoice?.invoiceNumber}
              {selectedInvoice && (
                <Badge className={`${STATUS_CONFIG[selectedInvoice.status]?.color} rounded-sm`}>
                  {STATUS_CONFIG[selectedInvoice.status]?.label}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6 py-4">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Bill To</p>
                  <p className="font-bold text-lg">{selectedInvoice.customerName}</p>
                  {selectedInvoice.customerAddress && (
                    <p className="text-sm text-slate-600 whitespace-pre-line">{selectedInvoice.customerAddress}</p>
                  )}
                  {selectedInvoice.customerEmail && (
                    <p className="text-sm text-slate-600">{selectedInvoice.customerEmail}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase text-slate-500 font-bold">Invoice Date</p>
                  <p className="font-bold">{selectedInvoice.invoiceDate}</p>
                  <p className="text-xs uppercase text-slate-500 font-bold mt-2">Due Date</p>
                  <p className="font-bold">{selectedInvoice.dueDate}</p>
                </div>
              </div>

              {/* Jobs */}
              {selectedInvoice.jobNumbers?.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold mb-1">Jobs Included</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedInvoice.jobNumbers.map((jn, i) => (
                      <Badge key={`badge-${i}`} variant="outline" className="rounded-sm font-mono">{jn}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div className="border-2 border-slate-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.lineItems?.map((item, i) => (
                      <TableRow key={`row-${i}`}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="font-mono">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  {selectedInvoice.taxRate > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Tax ({selectedInvoice.taxRate}%):</span>
                      <span className="font-mono">{formatCurrency(selectedInvoice.taxAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-bold">Total:</span>
                    <span className="font-mono font-bold text-lg">{formatCurrency(selectedInvoice.totalAmount)}</span>
                  </div>
                  {selectedInvoice.paidAmount && (
                    <div className="flex justify-between text-green-600">
                      <span>Paid:</span>
                      <span className="font-mono">{formatCurrency(selectedInvoice.paidAmount)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold mb-1">Notes</p>
                  <p className="text-sm text-slate-600">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-end border-t pt-4">
                <Button
                  onClick={() => handleDownloadPDF(selectedInvoice.id)}
                  variant="outline"
                  className="border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                {selectedInvoice.status === 'DRAFT' && (
                  <Button
                    onClick={() => handleUpdateStatus(selectedInvoice.id, 'SENT')}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                    data-testid="mark-sent-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Mark as Sent
                  </Button>
                )}
                {(selectedInvoice.status === 'SENT' || selectedInvoice.status === 'OVERDUE') && (
                  <Button
                    onClick={() => handleUpdateStatus(
                      selectedInvoice.id, 
                      'PAID', 
                      format(new Date(), 'yyyy-MM-dd'),
                      selectedInvoice.totalAmount
                    )}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                    data-testid="mark-paid-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
                {selectedInvoice.status === 'SENT' && (
                  <Button
                    onClick={() => handleUpdateStatus(selectedInvoice.id, 'OVERDUE')}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm"
                    data-testid="mark-overdue-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Mark as Overdue
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice <strong>{deletingInvoice?.invoiceNumber}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteInvoice}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
              data-testid="confirm-delete-invoice"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1A3668]" />
              Send Invoice via Email
            </DialogTitle>
            <DialogDescription>
              Send invoice {emailingInvoice?.invoiceNumber} to customer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Recipient Email *
              </Label>
              <Input
                type="email"
                value={emailForm.recipientEmail}
                onChange={(e) => setEmailForm({ ...emailForm, recipientEmail: e.target.value })}
                placeholder="customer@example.com"
                className="mt-2 rounded-sm border-2"
                data-testid="email-recipient-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Subject (optional)
              </Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder={`Invoice ${emailingInvoice?.invoiceNumber} from [Your Company]`}
                className="mt-2 rounded-sm border-2"
                data-testid="email-subject-input"
              />
              <p className="text-xs text-slate-500 mt-1">Leave blank to use default subject</p>
            </div>
            
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Message (optional)
              </Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Custom message to include in the email..."
                className="mt-2 rounded-sm border-2"
                rows={4}
                data-testid="email-message-input"
              />
              <p className="text-xs text-slate-500 mt-1">Leave blank to use default message with payment details</p>
            </div>

            <div className="bg-slate-50 p-3 rounded-sm">
              <p className="text-sm text-slate-600">
                <strong>Note:</strong> The invoice PDF will be attached to the email automatically.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipientEmail}
              className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
